import React from 'react';
import { Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import ornerLogo from '../../../../../../static/common/orner-logo.png';
import { BpInvoicePdfInput } from './invoicePdfTypes';
import {
    buildInvoicePdfViewModel,
    fmtMoney,
    INVOICE_PAYMENT_INSTRUCTIONS,
    InvoicePartyBlock,
    resolveInvoiceLetterhead
} from './invoicePdfLayout';

Font.register({ family: 'Rubik', src: 'https://fonts.gstatic.com/s/rubik/v3/4sMyW_teKWHB3K8Hm-Il6A.ttf' });

const BLUE = '#1a4f8b';
const BLUE_LIGHT = '#dbe6f0';
const BORDER = '#9fb3c8';
const TEXT = '#1f2937';
const MUTED = '#6b7280';
const RED = '#c0392b';

const styles = StyleSheet.create({
    page: {
        paddingTop: 9,
        paddingBottom: 9,
        paddingHorizontal: 20,
        fontSize: 8,
        fontFamily: 'Rubik',
        color: TEXT
    },
    headerRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 2 },
    logoImg: { width: 140, height: 47.7, marginTop: 2 },
    companyBlock: { flex: 1, alignItems: 'flex-end', paddingRight: 8 },
    companyName: { fontSize: 10, fontWeight: 700, color: BLUE, textAlign: 'right' },
    companyNameHe: { fontSize: 9, fontWeight: 700, color: BLUE, textAlign: 'right' },
    companyLine: { fontSize: 7, color: TEXT, textAlign: 'right', marginTop: 1 },
    logoPlaceholder: {
        width: 58,
        height: 42,
        borderWidth: 1,
        borderStyle: 'dashed',
        borderColor: BORDER,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
    },
    logoPlaceholderText: { fontSize: 5.5, color: MUTED, textAlign: 'center' },
    titleRow: { flexDirection: 'row', alignItems: 'baseline', marginTop: 2, marginBottom: 5 },
    title: { fontSize: 16, fontWeight: 700 },
    titleHe: { fontSize: 11, fontWeight: 700, marginLeft: 6 },
    metaTopRow: { flexDirection: 'row', borderWidth: 1, borderColor: BORDER, marginBottom: 3 },
    metaBox: { flex: 1, paddingVertical: 3, paddingHorizontal: 6, borderRightWidth: 1, borderColor: BORDER },
    metaBoxLast: { flex: 1, paddingVertical: 3, paddingHorizontal: 6 },
    metaBoxLabel: { fontSize: 6.5, color: MUTED },
    metaBoxValue: { fontSize: 9, fontWeight: 700, color: BLUE, marginTop: 1 },
    threeColRow: { flexDirection: 'row', gap: 6, marginBottom: 3 },
    infoTable: { width: '28%', borderWidth: 1, borderColor: BORDER },
    infoRow: { flexDirection: 'row', borderBottomWidth: 1, borderColor: BORDER, minHeight: 15 },
    infoRowLast: { flexDirection: 'row', minHeight: 15 },
    infoLabelCell: {
        width: '58%',
        paddingVertical: 2,
        paddingHorizontal: 4,
        borderRightWidth: 1,
        borderColor: BORDER,
        justifyContent: 'center'
    },
    infoValueCell: { width: '42%', paddingVertical: 2, paddingHorizontal: 4, justifyContent: 'center' },
    infoLabelText: { fontSize: 6, color: MUTED },
    infoValueText: { fontSize: 7.5, fontWeight: 700 },
    partyBox: { flex: 1, borderWidth: 1, borderColor: BORDER, padding: 5 },
    partyTitleRow: { flexDirection: 'row', alignItems: 'baseline', marginBottom: 3 },
    partyTitle: { fontSize: 8.5, fontWeight: 700, color: BLUE },
    partyTitleHe: { fontSize: 7, color: BLUE, marginLeft: 4 },
    partyFieldLabel: { fontSize: 5.5, color: MUTED },
    partyFieldValue: { fontSize: 7.5, marginTop: 1, marginBottom: 1 },
    sectionHeader: {
        backgroundColor: BLUE_LIGHT,
        borderWidth: 1,
        borderColor: BORDER,
        paddingVertical: 2,
        paddingHorizontal: 6,
        flexDirection: 'row',
        justifyContent: 'space-between'
    },
    sectionHeaderText: { fontSize: 8, fontWeight: 700, color: BLUE },
    shipmentTable: { borderWidth: 1, borderTopWidth: 0, borderColor: BORDER, marginBottom: 5 },
    shipmentRow: { flexDirection: 'row', borderBottomWidth: 1, borderColor: BORDER, minHeight: 15 },
    shipmentRowLast: { flexDirection: 'row', minHeight: 15 },
    shipmentCell: {
        width: '33.34%',
        paddingVertical: 1,
        paddingHorizontal: 6,
        borderRightWidth: 1,
        borderColor: BORDER,
        justifyContent: 'center'
    },
    shipmentCellLast: { width: '33.32%', paddingVertical: 1, paddingHorizontal: 6, justifyContent: 'center' },
    shipmentLabel: { fontSize: 5.5, color: MUTED },
    shipmentValue: { fontSize: 7, marginTop: 1 },
    table: { borderWidth: 1, borderColor: BORDER, marginBottom: 5 },
    tableHead: {
        flexDirection: 'row',
        backgroundColor: BLUE_LIGHT,
        borderBottomWidth: 1,
        borderColor: BORDER,
        paddingVertical: 3,
        paddingHorizontal: 2
    },
    tableHeadCellWrap: { borderRightWidth: 1, borderColor: BORDER, paddingHorizontal: 2 },
    tableHeadCell: { fontSize: 6, fontWeight: 700, color: BLUE, textAlign: 'center' },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderColor: '#e2e8f0',
        paddingVertical: 1,
        paddingHorizontal: 2,
        minHeight: 12
    },
    tableCellWrap: { borderRightWidth: 1, borderColor: '#e2e8f0', paddingHorizontal: 2 },
    tableCell: { fontSize: 6.5, textAlign: 'center' },
    tableCellLeft: { fontSize: 6.5, textAlign: 'left' },
    tableTotalRow: { flexDirection: 'row', backgroundColor: BLUE_LIGHT, paddingVertical: 3, paddingHorizontal: 2 },
    summaryRow: { flexDirection: 'row', gap: 6, marginBottom: 3 },
    summaryBox: { flex: 1, borderWidth: 1, borderColor: BORDER, padding: 5 },
    summaryTitle: { fontSize: 7.5, fontWeight: 700, color: BLUE, marginBottom: 3 },
    summaryLine: { fontSize: 7, marginBottom: 1 },
    summaryTotalLine: { fontSize: 8, fontWeight: 700 },
    amountDueBox: { width: '26%', borderWidth: 1.5, borderColor: BLUE, padding: 6, justifyContent: 'center' },
    amountDueLabel: { fontSize: 7.5, fontWeight: 700, color: BLUE, marginBottom: 3 },
    amountDueValue: { fontSize: 14, fontWeight: 700 },
    paymentTable: { borderWidth: 1, borderTopWidth: 0, borderColor: BORDER, flexDirection: 'row', marginBottom: 3 },
    paymentCol: { flex: 1, padding: 5, borderRightWidth: 1, borderColor: BORDER },
    paymentColLast: { flex: 1, padding: 5, borderRightWidth: 1, borderColor: BORDER },
    paymentLine: { fontSize: 7, marginBottom: 1 },
    qrCol: { width: 68, padding: 5, alignItems: 'center', justifyContent: 'center' },
    qrBox: { width: 46, height: 46, borderWidth: 1, borderColor: BORDER, alignItems: 'center', justifyContent: 'center' },
    qrText: { fontSize: 5.5, color: MUTED, textAlign: 'center', marginTop: 2 },
    notesRow: { flexDirection: 'row', gap: 6, marginBottom: 4 },
    notesBlock: { flex: 1 },
    notesTitle: { fontSize: 7, fontWeight: 700, color: BLUE, marginBottom: 2 },
    notesText: { fontSize: 5.8, lineHeight: 1.25, color: MUTED },
    footerRow: {
        marginTop: 2,
        borderTopWidth: 1,
        borderColor: BORDER,
        paddingTop: 4,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-end'
    },
    footerSide: { fontSize: 6.5, color: MUTED, width: '30%' },
    footerCenter: { fontSize: 8, fontWeight: 700, color: BLUE, textAlign: 'center', width: '40%' }
});

const PartyField = ({ label, labelHe, value }: { label: string; labelHe: string; value: string }) => (
    <View>
        <Text style={styles.partyFieldLabel}>
            {label} / {labelHe}
        </Text>
        <Text style={styles.partyFieldValue}>{value}</Text>
    </View>
);

const PartyBox = ({ title, titleHe, party }: { title: string; titleHe: string; party: InvoicePartyBlock }) => (
    <View style={styles.partyBox} wrap={false}>
        <View style={styles.partyTitleRow}>
            <Text style={styles.partyTitle}>{title}</Text>
            <Text style={styles.partyTitleHe}>{titleHe}</Text>
        </View>
        <PartyField label="Company Name" labelHe="שם החברה" value={party.companyName} />
        <PartyField label="Contact Person" labelHe="איש קשר" value={party.contactPerson} />
        <PartyField label="Address" labelHe="כתובת" value={party.addressLine} />
        <PartyField label="City, Zip" labelHe="עיר, מיקוד" value={party.cityZip} />
        <PartyField label="Country" labelHe="מדינה" value={party.country} />
        <PartyField label="Tel / Phone" labelHe="טלפון" value={party.phone} />
        <PartyField label="Vat No." labelHe="עוסק מורשה" value={party.vat} />
    </View>
);

const InfoRow = ({ label, labelHe, value, last }: { label: string; labelHe: string; value: string; last?: boolean }) => (
    <View style={last ? styles.infoRowLast : styles.infoRow} wrap={false}>
        <View style={styles.infoLabelCell}>
            <Text style={styles.infoLabelText}>
                {label} / {labelHe}
            </Text>
        </View>
        <View style={styles.infoValueCell}>
            <Text style={styles.infoValueText}>{value}</Text>
        </View>
    </View>
);

const CHARGE_COLUMNS: Array<{
    key:
        | 'no'
        | 'description'
        | 'fromDate'
        | 'toDate'
        | 'currency'
        | 'exchangeRate'
        | 'unit'
        | 'qty'
        | 'unitPrice'
        | 'amountExclVat'
        | 'vatPercent'
        | 'vatAmount'
        | 'amountInclVat';
    label: string;
    labelHe: string;
    width: string;
    align?: 'left' | 'center';
}> = [
    { key: 'no', label: 'No.', labelHe: "מ'", width: '4%' },
    { key: 'description', label: 'Description', labelHe: 'תיאור', width: '19%', align: 'left' },
    { key: 'fromDate', label: 'From', labelHe: 'מתאריך', width: '7%' },
    { key: 'toDate', label: 'To', labelHe: 'עד תאריך', width: '7%' },
    { key: 'currency', label: 'Currency', labelHe: 'מטבע', width: '6%' },
    { key: 'exchangeRate', label: 'Ex. Rate', labelHe: 'שער', width: '6%' },
    { key: 'unit', label: 'Unit', labelHe: 'יחידה', width: '7%' },
    { key: 'qty', label: 'Qty', labelHe: 'כמות', width: '4%' },
    { key: 'unitPrice', label: 'Unit Price', labelHe: 'מחיר יחידה', width: '8%' },
    { key: 'amountExclVat', label: 'Excl. VAT', labelHe: 'ללא מע"מ', width: '8%' },
    { key: 'vatPercent', label: 'VAT %', labelHe: 'מע"מ %', width: '5%' },
    { key: 'vatAmount', label: 'VAT Amt', labelHe: 'סכום מע"מ', width: '7%' },
    { key: 'amountInclVat', label: 'Incl. VAT', labelHe: 'כולל מע"מ', width: '8%' }
];

type ChargeRow = ReturnType<typeof buildInvoicePdfViewModel>['chargeRows'][0];

const formatCell = (row: ChargeRow, key: (typeof CHARGE_COLUMNS)[number]['key']) => {
    const value = row[key];
    if (key === 'unitPrice' || key === 'amountExclVat' || key === 'vatAmount' || key === 'amountInclVat') {
        return Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    if (key === 'vatPercent') return `${value}%`;
    return String(value ?? '—');
};

const BpInvoicePdfDocument: React.FC<BpInvoicePdfInput> = (input) => {
    const vm = buildInvoicePdfViewModel(input);
    const letterhead = resolveInvoiceLetterhead(input.issuer);
    const accent = vm.isCreditNote ? RED : BLUE;
    const maxDetailRows = Math.max(...vm.shipmentDetailColumns.map((col) => col.length));
    const issuerLogoSrc = letterhead.logoUrl || (letterhead.isOrnerTenant ? ornerLogo : undefined);

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={styles.headerRow} wrap={false}>
                    {issuerLogoSrc ? (
                        <Image src={issuerLogoSrc} style={styles.logoImg} />
                    ) : (
                        <View style={[styles.logoPlaceholder, { width: 140, height: 48 }]}>
                            <Text style={styles.logoPlaceholderText}>Issuer Logo{'\n'}לוגו המנפיק</Text>
                        </View>
                    )}
                    <View style={styles.companyBlock}>
                        <Text style={styles.companyName}>{letterhead.name}</Text>
                        {letterhead.nameHe ? <Text style={styles.companyNameHe}>{letterhead.nameHe}</Text> : null}
                        <Text style={styles.companyLine}>{letterhead.addressLine}</Text>
                        {letterhead.addressLineHe ? (
                            <Text style={styles.companyLine}>{letterhead.addressLineHe}</Text>
                        ) : null}
                        <Text style={styles.companyLine}>
                            Tel: {letterhead.tel}
                            {letterhead.fax ? ` / Fax: ${letterhead.fax}` : ''}
                        </Text>
                        <Text style={styles.companyLine}>
                            Email: {letterhead.email}
                            {letterhead.website ? ` / Website: ${letterhead.website}` : ''}
                        </Text>
                    </View>
                    <View style={styles.logoPlaceholder}>
                        <Text style={styles.logoPlaceholderText}>Company Logo{'\n'}לוגו החברה</Text>
                    </View>
                </View>

                <View style={styles.titleRow} wrap={false}>
                    <Text style={[styles.title, { color: accent }]}>{vm.documentTitle}</Text>
                    <Text style={[styles.titleHe, { color: accent }]}>/ {vm.documentTitleHe}</Text>
                </View>

                <View style={styles.metaTopRow} wrap={false}>
                    <View style={styles.metaBox}>
                        <Text style={styles.metaBoxLabel}>Invoice No. / מספר חשבונית</Text>
                        <Text style={[styles.metaBoxValue, { color: accent }]}>{vm.invoiceNumber}</Text>
                    </View>
                    <View style={styles.metaBox}>
                        <Text style={styles.metaBoxLabel}>Invoice Date / תאריך חשבונית</Text>
                        <Text style={styles.metaBoxValue}>{vm.invoiceDate}</Text>
                    </View>
                    <View style={styles.metaBoxLast}>
                        <Text style={styles.metaBoxLabel}>Due Date / תאריך ערך</Text>
                        <Text style={styles.metaBoxValue}>{vm.dueDate}</Text>
                    </View>
                </View>

                <View style={styles.threeColRow} wrap={false}>
                    <View style={styles.infoTable}>
                        <InfoRow label="Customer No." labelHe="מספר לקוח" value={vm.customerNo} />
                        <InfoRow label="Customer Vat No." labelHe="עוסק מורשה" value={vm.customerVat} />
                        <InfoRow label="Branch No." labelHe="מספר סניף" value={vm.branchNo} />
                        <InfoRow label="Reference" labelHe="אסמכתא" value={vm.reference} />
                        <InfoRow label="Payment Terms" labelHe="תנאי תשלום" value={vm.paymentTerms} />
                        <InfoRow label="Currency" labelHe="מטבע" value={vm.currency} />
                        <InfoRow label="Incoterm" labelHe="תנאי סחר" value={vm.incoterm} />
                        <InfoRow label="Sales Person" labelHe="איש מכירות" value={vm.salesPerson} last />
                    </View>
                    <PartyBox title="Bill To" titleHe="לכתובת חיוב" party={vm.billTo} />
                    <PartyBox title="Ship To" titleHe="לכתובת אספקה" party={vm.shipTo} />
                </View>

                <View style={styles.sectionHeader} wrap={false}>
                    <Text style={styles.sectionHeaderText}>Shipment Details</Text>
                    <Text style={styles.sectionHeaderText}>פרטי משלוח</Text>
                </View>
                <View style={styles.shipmentTable}>
                    {Array.from({ length: maxDetailRows }).map((_, rowIndex) => {
                        const isLast = rowIndex === maxDetailRows - 1;
                        return (
                            <View style={isLast ? styles.shipmentRowLast : styles.shipmentRow} key={rowIndex} wrap={false}>
                                {vm.shipmentDetailColumns.map((col, colIndex) => {
                                    const field = col[rowIndex];
                                    const isLastCol = colIndex === vm.shipmentDetailColumns.length - 1;
                                    return (
                                        <View style={isLastCol ? styles.shipmentCellLast : styles.shipmentCell} key={colIndex}>
                                            {field ? (
                                                <>
                                                    <Text style={styles.shipmentLabel}>
                                                        {field.label} / {field.labelHe}
                                                    </Text>
                                                    <Text style={styles.shipmentValue}>{field.value}</Text>
                                                </>
                                            ) : null}
                                        </View>
                                    );
                                })}
                            </View>
                        );
                    })}
                </View>

                <View style={styles.sectionHeader} wrap={false}>
                    <Text style={styles.sectionHeaderText}>Charge Details</Text>
                    <Text style={styles.sectionHeaderText}>פירוט חיובים</Text>
                </View>
                <View style={styles.table}>
                    <View style={styles.tableHead} wrap={false}>
                        {CHARGE_COLUMNS.map((col, i) => (
                            <View
                                key={col.key}
                                style={i < CHARGE_COLUMNS.length - 1 ? [styles.tableHeadCellWrap, { width: col.width }] : { width: col.width }}
                            >
                                <Text style={styles.tableHeadCell}>{col.label}</Text>
                                <Text style={[styles.tableHeadCell, { fontWeight: 400 }]}>{col.labelHe}</Text>
                            </View>
                        ))}
                    </View>
                    {vm.chargeRows.map((row) => (
                        <View style={styles.tableRow} key={row.no} wrap={false}>
                            {CHARGE_COLUMNS.map((col, i) => (
                                <View
                                    key={col.key}
                                    style={i < CHARGE_COLUMNS.length - 1 ? [styles.tableCellWrap, { width: col.width }] : { width: col.width }}
                                >
                                    <Text style={col.align === 'left' ? styles.tableCellLeft : styles.tableCell}>
                                        {formatCell(row, col.key)}
                                    </Text>
                                </View>
                            ))}
                        </View>
                    ))}
                    <View style={styles.tableTotalRow} wrap={false}>
                        <View style={{ width: '57%' }}>
                            <Text style={{ fontSize: 7, fontWeight: 700, color: BLUE }}>Total / סה"כ</Text>
                        </View>
                        <View style={{ width: '8%' }}>
                            <Text style={styles.tableCell}>
                                {vm.totals.exclVat.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Text>
                        </View>
                        <View style={{ width: '5%' }} />
                        <View style={{ width: '7%' }}>
                            <Text style={styles.tableCell}>
                                {vm.totals.vatAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Text>
                        </View>
                        <View style={{ width: '8%' }}>
                            <Text style={styles.tableCell}>
                                {vm.totals.inclVat.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Text>
                        </View>
                    </View>
                </View>

                <View style={styles.summaryRow} wrap={false}>
                    <View style={styles.summaryBox}>
                        <Text style={styles.summaryTitle}>Total Summary / סיכום סכומים</Text>
                        <Text style={styles.summaryLine}>Total (Excl. VAT) / סה"כ לפני מע"מ: {fmtMoney(vm.totals.exclVat, vm.currency)}</Text>
                        <Text style={styles.summaryLine}>
                            VAT {vm.totals.vatPercent}% / מע"מ: {fmtMoney(vm.totals.vatAmount, vm.currency)}
                        </Text>
                        <Text style={styles.summaryTotalLine}>Total (Incl. VAT) / סה"כ כולל מע"מ: {fmtMoney(vm.totals.inclVat, vm.currency)}</Text>
                    </View>
                    <View style={styles.summaryBox}>
                        <Text style={styles.summaryTitle}>Breakdown by Currency / פירוט לפי מטבע</Text>
                        {vm.currencyBreakdown.map((row) => (
                            <Text key={row.currency} style={styles.summaryLine}>
                                {row.currency} — Excl. VAT: {fmtMoney(row.exclVat, row.currency)} | VAT: {fmtMoney(row.vatAmount, row.currency)}
                            </Text>
                        ))}
                    </View>
                    <View style={styles.amountDueBox}>
                        <Text style={styles.amountDueLabel}>Amount Due / סכום לתשלום</Text>
                        <Text style={[styles.amountDueValue, { color: accent }]}>{vm.amountDueLabel}</Text>
                    </View>
                </View>

                <View style={styles.sectionHeader} wrap={false}>
                    <Text style={styles.sectionHeaderText}>Payment Instructions</Text>
                    <Text style={styles.sectionHeaderText}>הוראות תשלום</Text>
                </View>
                <View style={styles.paymentTable} wrap={false}>
                    {letterhead.isOrnerTenant ? (
                        <>
                            <View style={styles.paymentCol}>
                                <Text style={styles.paymentLine}>Bank Name / שם הבנק</Text>
                                <Text style={[styles.paymentLine, { fontWeight: 700 }]}>
                                    {INVOICE_PAYMENT_INSTRUCTIONS.bankName} / {INVOICE_PAYMENT_INSTRUCTIONS.bankNameHe}
                                </Text>
                                <Text style={styles.paymentLine}>Bank Address / כתובת הבנק</Text>
                                <Text style={[styles.paymentLine, { fontWeight: 700 }]}>
                                    {INVOICE_PAYMENT_INSTRUCTIONS.bankAddress}
                                </Text>
                                <Text style={styles.paymentLine}>Beneficiary / מוטב</Text>
                                <Text style={[styles.paymentLine, { fontWeight: 700 }]}>
                                    {INVOICE_PAYMENT_INSTRUCTIONS.beneficiary}
                                </Text>
                            </View>
                            <View style={styles.paymentColLast}>
                                <Text style={styles.paymentLine}>Bank No. / מספר בנק: {INVOICE_PAYMENT_INSTRUCTIONS.bankNo}</Text>
                                <Text style={styles.paymentLine}>
                                    Branch No. / מספר סניף: {INVOICE_PAYMENT_INSTRUCTIONS.branchNo}
                                </Text>
                                <Text style={styles.paymentLine}>
                                    Account No. / מספר חשבון: {INVOICE_PAYMENT_INSTRUCTIONS.accountNo}
                                </Text>
                                <Text style={styles.paymentLine}>IBAN: {INVOICE_PAYMENT_INSTRUCTIONS.iban}</Text>
                                <Text style={styles.paymentLine}>SWIFT: {INVOICE_PAYMENT_INSTRUCTIONS.swift}</Text>
                            </View>
                        </>
                    ) : (
                        <>
                            <View style={styles.paymentCol}>
                                <Text style={styles.paymentLine}>Bank Name / שם הבנק</Text>
                                <Text style={[styles.paymentLine, { fontWeight: 700 }]}>—</Text>
                                <Text style={styles.paymentLine}>Bank Address / כתובת הבנק</Text>
                                <Text style={[styles.paymentLine, { fontWeight: 700 }]}>—</Text>
                                <Text style={styles.paymentLine}>Beneficiary / מוטב</Text>
                                <Text style={[styles.paymentLine, { fontWeight: 700 }]}>{letterhead.name}</Text>
                            </View>
                            <View style={styles.paymentColLast}>
                                <Text style={styles.paymentLine}>Bank No. / מספר בנק: —</Text>
                                <Text style={styles.paymentLine}>Branch No. / מספר סניף: —</Text>
                                <Text style={styles.paymentLine}>Account No. / מספר חשבון: —</Text>
                                <Text style={styles.paymentLine}>IBAN: —</Text>
                                <Text style={styles.paymentLine}>SWIFT: —</Text>
                            </View>
                        </>
                    )}
                    <View style={styles.qrCol}>
                        <View style={styles.qrBox} />
                        <Text style={styles.qrText}>QR Code{'\n'}קוד לתשלום</Text>
                    </View>
                </View>

                <View style={styles.notesRow} wrap={false}>
                    <View style={styles.notesBlock}>
                        <Text style={styles.notesTitle}>Notes / הערות</Text>
                        <Text style={styles.notesText}>
                            • Please make payment within the payment terms stated above.{'\n'}
                            תשלום יש לבצע במסגרת תנאי התשלום הנקובים לעיל{'\n'}
                            • Late payments may accrue interest as per credit agreement.{'\n'}
                            תשלומים באיחור עלולים לחייב בריבית פיגורים בהתאם להסכם האשראי{'\n'}
                            • This invoice is computer generated and does not require a signature.{'\n'}
                            חשבונית זו הופקה באמצעי ממוחשב ואינה דורשת חתימה{'\n'}
                            • For any queries please contact: {letterhead.accountingEmail} / {letterhead.tel}
                        </Text>
                    </View>
                    <View style={styles.notesBlock}>
                        <Text style={styles.notesTitle}>Terms &amp; Conditions / תנאים</Text>
                        <Text style={styles.notesText}>
                            1. All charges are in accordance with our standard trading conditions.{'\n'}
                            כל החיובים הינם בהתאם לתנאי המסחר הסטנדרטיים שלנו{'\n'}
                            2. Goods are carried at owner&apos;s risk.{'\n'}
                            הסחורה מובלת באחריות הבעלים{'\n'}
                            3. Disputes are subject to the jurisdiction of Israeli courts.{'\n'}
                            מחלוקות כפופות לסמכות השיפוט של בתי המשפט בישראל
                        </Text>
                    </View>
                </View>

                <View style={styles.footerRow} wrap={false}>
                    <Text style={styles.footerSide}>Authorized Signature / חתימה מוסמכת</Text>
                    <Text style={styles.footerCenter}>THANK YOU FOR YOUR BUSINESS!</Text>
                    <Text style={[styles.footerSide, { textAlign: 'right' }]}>{letterhead.name}</Text>
                </View>
            </Page>
        </Document>
    );
};

export default BpInvoicePdfDocument;
